"use client";

import React, { useEffect, useRef, useState } from "react";

import { Button } from "@heroui/button";
import { Card, CardBody, CardHeader } from "@heroui/card";
import { Input } from "@heroui/input";
import { Link } from "@heroui/link";

import type { OrganizationTerminology } from "@/lib/services/terminologyService";

interface Organization {
  id: string;
  name: string;
  displayName?: string;
  logoUrl?: string;
  slug?: string;
  websiteUrl?: string;
  contactEmail?: string;
  terminology?: OrganizationTerminology;
}

interface OrganizationSearchProps {
  onContinueWithNew?: () => void;
}

async function fetchOrganizations(query: string): Promise<Organization[]> {
  const response = await fetch(
    `/api/organizations/search?q=${encodeURIComponent(query)}`,
  );
  const data = (await response.json()) as { organizations?: Organization[] };

  return data.organizations ?? [];
}

export function OrganizationSearch({
  onContinueWithNew,
}: OrganizationSearchProps) {
  const [query, setQuery] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>(null);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 2) {
      setOrganizations([]);
      setShowResults(false);

      return;
    }

    setIsLoading(true);
    searchTimeoutRef.current = setTimeout(() => {
      void fetchOrganizations(query)
        .then((orgs) => {
          setOrganizations(orgs);
          setShowResults(true);
        })
        .catch((error) => {
          console.error("Error searching organizations:", error);
          setOrganizations([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query]);

  const handleOrganizationSelect = (organization: Organization) => {
    setSelectedOrg(organization);
    setShowResults(false);
  };

  const handleContinueWithNew = () => {
    setSelectedOrg(null);
    setQuery("");
    setShowResults(false);
    onContinueWithNew?.();
  };

  if (selectedOrg) {
    return (
      <SelectedOrgCard
        org={selectedOrg}
        onSearchAgain={() => setSelectedOrg(null)}
      />
    );
  }

  return (
    <OrgSearchPanel
      isLoading={isLoading}
      organizations={organizations}
      query={query}
      showResults={showResults}
      onContinueWithNew={handleContinueWithNew}
      onOrganizationSelect={handleOrganizationSelect}
      onQueryChange={setQuery}
    />
  );
}

function OrgLogo({
  logoUrl,
  name,
  size,
}: {
  name: string;
  logoUrl: string;
  size: "sm" | "md";
}) {
  const sizeClass = size === "md" ? "w-8 h-8" : "w-6 h-6";

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      alt={`${name} logo`}
      className={`${sizeClass} rounded-full object-cover`}
      src={logoUrl}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}

interface JoinOptionLabels {
  caseWorkerLabel: string;
  needsLabel: string;
  volunteerLabel: string;
  volunteersLabel: string;
}

function defaultCaseWorkerLabel(
  t: OrganizationTerminology | undefined,
): string {
  return t?.caseWorker?.singular ?? "Case worker";
}

function defaultNeedPlural(t: OrganizationTerminology | undefined): string {
  return t?.need?.plural ?? "needs";
}

function defaultVolunteerSingular(
  t: OrganizationTerminology | undefined,
): string {
  return t?.volunteer?.singular ?? "Volunteer";
}

function defaultVolunteerPlural(
  t: OrganizationTerminology | undefined,
): string {
  return (t?.volunteer?.plural ?? "volunteers").toLowerCase();
}

function getJoinOptionLabels(
  t: OrganizationTerminology | undefined,
): JoinOptionLabels {
  return {
    caseWorkerLabel: defaultCaseWorkerLabel(t),
    needsLabel: defaultNeedPlural(t),
    volunteerLabel: defaultVolunteerSingular(t),
    volunteersLabel: defaultVolunteerPlural(t),
  };
}

function VolunteerOptionCard({
  labels,
  org,
}: {
  labels: JoinOptionLabels;
  org: Organization;
}) {
  return (
    <div
      className="rounded-lg border-2 border-green-300 bg-green-100 p-4"
      data-testid="volunteer-cta"
    >
      <p className="text-sm font-semibold text-green-900 mb-1">
        Want to {labels.volunteerLabel.toLowerCase()}?
      </p>
      <p className="text-sm text-green-800 mb-3">
        View {labels.needsLabel} and respond—no account needed to browse.
      </p>
      <Button
        as="a"
        color="success"
        href={`/${org.slug}/needs`}
        rel="noopener noreferrer"
        size="sm"
        target="_blank"
      >
        View this organization&apos;s {labels.needsLabel}
      </Button>
    </div>
  );
}

function CaseWorkerOptionCard({
  labels,
  org,
}: {
  labels: JoinOptionLabels;
  org: Organization;
}) {
  return (
    <div className="rounded-lg border-2 border-green-300 bg-green-100 p-4">
      <p className="text-sm font-semibold text-green-900 mb-1">
        {labels.caseWorkerLabel}?
      </p>
      <p className="text-sm text-green-800 mb-3">
        Register to help manage {labels.needsLabel} and connect{" "}
        {labels.volunteersLabel}.
      </p>
      <Button
        as="a"
        color="success"
        href={`/${org.slug}/register-case-worker`}
        size="sm"
        variant="bordered"
      >
        Register as {labels.caseWorkerLabel.toLowerCase()}
      </Button>
    </div>
  );
}

function CoordinatorOptionCard({ org }: { org: Organization }) {
  return (
    <div className="rounded-lg border-2 border-green-300 bg-green-100 p-4">
      <p className="text-sm font-semibold text-green-900 mb-1">Coordinator?</p>
      <p className="text-sm text-green-800 mb-3">
        Coordinators are invited by the organization. Contact the administrator
        to ask about joining.
      </p>
      {org.contactEmail && (
        <Button
          as="a"
          color="success"
          href={`mailto:${org.contactEmail}?subject=Request to join as coordinator – ${org.name}&body=Hi, I would like to join your organization on NeedBridge as a coordinator. Could you please send me an invitation?`}
          size="sm"
          variant="bordered"
        >
          Contact administrator
        </Button>
      )}
    </div>
  );
}

function JoinOptions({ org }: { org: Organization }) {
  if (!org.slug) return null;

  const labels = getJoinOptionLabels(org.terminology);

  return (
    <div className="space-y-3" data-testid="join-options">
      <p className="text-sm font-semibold text-green-900">
        How would you like to get involved?
      </p>
      <div className="grid gap-3 sm:grid-cols-1">
        <VolunteerOptionCard labels={labels} org={org} />
        <CaseWorkerOptionCard labels={labels} org={org} />
        <CoordinatorOptionCard org={org} />
      </div>
    </div>
  );
}

function SelectedOrgCard({
  onSearchAgain,
  org,
}: {
  org: Organization;
  onSearchAgain: () => void;
}) {
  return (
    <Card className="border-green-200 bg-green-50">
      <CardHeader>
        <div className="flex items-center gap-3">
          {org.logoUrl && (
            <OrgLogo logoUrl={org.logoUrl} name={org.name} size="md" />
          )}
          <div>
            <h3 className="font-semibold text-green-800">
              {org.displayName ?? org.name}
            </h3>
            <p className="text-sm text-green-600">
              Found existing organization
            </p>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className="space-y-3">
          <JoinOptions org={org} />
          <div className="flex flex-wrap gap-2">
            {org.contactEmail && (
              <Link
                className="text-sm text-green-600 hover:text-green-800 underline"
                href={`mailto:${org.contactEmail}?subject=Request to join ${org.name} on NeedBridge&body=Hi, I would like to join your organization on NeedBridge. Could you please send me an invitation?`}
              >
                Contact Administrator: {org.contactEmail}
              </Link>
            )}
            {org.websiteUrl && (
              <Link
                className="text-sm text-green-600 hover:text-green-800 underline"
                href={org.websiteUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                Visit Website
              </Link>
            )}
          </div>
          <Button
            className="text-green-600"
            size="sm"
            variant="light"
            onClick={onSearchAgain}
          >
            Search Again
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function OrgSearchResults({
  onSelect,
  organizations,
  query,
  showResults,
}: {
  organizations: Organization[];
  query: string;
  showResults: boolean;
  onSelect: (org: Organization) => void;
}) {
  if (!showResults) return null;

  if (organizations.length > 0) {
    return <OrgResultsList organizations={organizations} onSelect={onSelect} />;
  }

  if (query.length >= 2) {
    return (
      <p className="text-sm text-amber-600">
        No organizations found with that name. You can proceed to create a new
        one.
      </p>
    );
  }

  return null;
}

function OrgSearchPanel({
  isLoading,
  onContinueWithNew,
  onOrganizationSelect,
  onQueryChange,
  organizations,
  query,
  showResults,
}: {
  isLoading: boolean;
  organizations: Organization[];
  query: string;
  showResults: boolean;
  onContinueWithNew: () => void;
  onOrganizationSelect: (org: Organization) => void;
  onQueryChange: (q: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
        <h3 className="font-semibold text-amber-800 mb-2">
          Check for Existing Organizations
        </h3>
        <p className="text-sm text-amber-700 mb-3">
          Before creating a new organization, search to see if your organization
          already exists on NeedBridge. If it does, you&apos;ll need to request
          an invitation from the administrator instead of creating a duplicate.
        </p>

        <div className="space-y-3">
          <Input
            placeholder="Search for your organization name..."
            size="sm"
            startContent={
              <svg
                className="w-4 h-4 text-default-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            }
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
          />

          {isLoading && <p className="text-sm text-amber-600">Searching...</p>}

          <OrgSearchResults
            organizations={organizations}
            query={query}
            showResults={showResults}
            onSelect={onOrganizationSelect}
          />
        </div>
      </div>

      <div className="text-center">
        <Button
          color="primary"
          disabled={isLoading}
          variant="solid"
          onClick={onContinueWithNew}
        >
          Continue with New Organization
        </Button>
      </div>
    </div>
  );
}

function OrgResultsList({
  onSelect,
  organizations,
}: {
  organizations: Organization[];
  onSelect: (org: Organization) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-amber-800">
        Found {organizations.length} organization
        {organizations.length !== 1 ? "s" : ""}:
      </p>
      {organizations.map((org) => (
        <OrgResultCard key={org.id} org={org} onSelect={onSelect} />
      ))}
    </div>
  );
}

function OrgResultCard({
  onSelect,
  org,
}: {
  org: Organization;
  onSelect: (org: Organization) => void;
}) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(org);
    }
  };

  return (
    <div
      className="cursor-pointer hover:bg-amber-100 transition-colors border border-amber-200 rounded-md p-3"
      data-testid="organization-card"
      role="button"
      tabIndex={0}
      onClick={() => onSelect(org)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center gap-3">
        {org.logoUrl && (
          <OrgLogo logoUrl={org.logoUrl} name={org.name} size="sm" />
        )}
        <div>
          <p className="font-medium text-sm">{org.displayName ?? org.name}</p>
          {org.contactEmail && (
            <p className="text-xs text-default-500">{org.contactEmail}</p>
          )}
        </div>
      </div>
    </div>
  );
}
